import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { HubConnectionState } from '@microsoft/signalr';
import { BaseHubConnection } from './base-hub-connection.service';
import { GlobalVariablesService } from '../global-variables.service';
import { ChatMessageDto, ChatReadEvent, ChatTypingEvent } from '@app/core/models/chat/chat.models';

@Injectable({
    providedIn: 'root'
})
export class ChatHubConnectionService extends BaseHubConnection {
    private initialized = false;
    private joinedConversations = new Map<string, number>();

    private newMessageSubject = new Subject<ChatMessageDto>();
    public newMessage$ = this.newMessageSubject.asObservable();

    private readSubject = new Subject<ChatReadEvent>();
    public read$ = this.readSubject.asObservable();

    private typingStartedSubject = new Subject<ChatTypingEvent>();
    public typingStarted$ = this.typingStartedSubject.asObservable();

    private typingStoppedSubject = new Subject<ChatTypingEvent>();
    public typingStopped$ = this.typingStoppedSubject.asObservable();

    public connectionReady = new BehaviorSubject<boolean>(false);

    constructor(private globals: GlobalVariablesService) {
        super();
    }

    public init(): void {
        if (this.initialized) {
            return;
        }

        this.initializeHubConnection(`${this.globals.apiV2Address}/chat-hub`, { withCredentials: false });

        this.hubConnection.on('message:new', (message: ChatMessageDto) => {
            this.newMessageSubject.next(message);
        });

        this.hubConnection.on('message:read', (payload: ChatReadEvent) => {
            this.readSubject.next(payload);
        });

        this.hubConnection.on('typing:started', (payload: ChatTypingEvent) => {
            this.typingStartedSubject.next(payload);
        });

        this.hubConnection.on('typing:stopped', (payload: ChatTypingEvent) => {
            this.typingStoppedSubject.next(payload);
        });

        this.hubConnection.onreconnected(() => {
            this.connectionReady.next(true);
            this.rejoinAll();
        });

        this.connectionStatus.subscribe((connected) => {
            this.connectionReady.next(connected);
        });

        this.initialized = true;
        void this.startConnection();
    }

    public joinConversation(conversationId: string): void {
        if (!conversationId) {
            return;
        }

        const currentCount = this.joinedConversations.get(conversationId) ?? 0;
        this.joinedConversations.set(conversationId, currentCount + 1);
        if (currentCount > 0) {
            return;
        }

        if (this.currentState !== HubConnectionState.Connected) {
            void this.startConnection().then(() => this.sendMessage('JoinConversation', conversationId));
            return;
        }

        this.sendMessage('JoinConversation', conversationId);
    }

    public leaveConversation(conversationId: string): void {
        if (!conversationId) {
            return;
        }

        const currentCount = this.joinedConversations.get(conversationId);
        if (!currentCount) {
            return;
        }

        if (currentCount > 1) {
            this.joinedConversations.set(conversationId, currentCount - 1);
            return;
        }

        this.joinedConversations.delete(conversationId);

        if (this.currentState !== HubConnectionState.Connected) {
            return;
        }

        this.sendMessage('LeaveConversation', conversationId);
    }

    public sendTypingStarted(conversationId: string, userId: string, userName?: string): void {
        if (!conversationId || !userId) {
            return;
        }
        this.sendMessage('TypingStarted', conversationId, userId, userName ?? '');
    }

    public sendTypingStopped(conversationId: string, userId: string): void {
        if (!conversationId || !userId) {
            return;
        }
        this.sendMessage('TypingStopped', conversationId, userId);
    }

    private rejoinAll(): void {
        if (this.currentState !== HubConnectionState.Connected) {
            return;
        }

        this.joinedConversations.forEach((_count, conversationId) => {
            this.sendMessage('JoinConversation', conversationId);
        });
    }
}
